/**
 * ProgressChart — plain-SVG line chart for the tracker cards (no chart
 * dependency on web). Draws a smoothed series with a soft area fill, matching
 * the mobile chart's look.
 */
import { useId, useMemo } from 'react';
import './more.css';

const VIEWBOX_WIDTH = 320;
const VIEWBOX_HEIGHT = 120;
const PADDING_Y = 10;

interface ProgressChartProps {
  values: number[];
  labels: string[];
  /** CSS color for the line and the top of the area fill. */
  color: string;
  ariaLabel: string;
}

const buildPoints = (values: number[]): Array<{ x: number; y: number }> => {
  if (values.length === 0) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const usableHeight = VIEWBOX_HEIGHT - PADDING_Y * 2;

  return values.map((value, index) => ({
    x: values.length === 1 ? VIEWBOX_WIDTH / 2 : (index / (values.length - 1)) * VIEWBOX_WIDTH,
    y: PADDING_Y + (1 - (value - min) / span) * usableHeight,
  }));
};

/** Catmull-Rom control points, so the polyline reads as a smooth curve. */
const buildLinePath = (points: Array<{ x: number; y: number }>): string => {
  if (points.length === 0) return '';
  if (points.length === 1) {
    return `M 0 ${points[0].y} L ${VIEWBOX_WIDTH} ${points[0].y}`;
  }

  return points.reduce((path, point, index) => {
    if (index === 0) return `M ${point.x} ${point.y}`;
    const previous = points[index - 1];
    const controlX = (previous.x + point.x) / 2;
    return `${path} C ${controlX} ${previous.y}, ${controlX} ${point.y}, ${point.x} ${point.y}`;
  }, '');
};

export function ProgressChart({ values, labels, color, ariaLabel }: ProgressChartProps) {
  const gradientId = useId();
  const points = useMemo(() => buildPoints(values), [values]);
  const linePath = useMemo(() => buildLinePath(points), [points]);
  const areaPath = linePath
    ? `${linePath} L ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT} L 0 ${VIEWBOX_HEIGHT} Z`
    : '';

  const visibleLabels = useMemo(() => {
    const filled = labels.filter(Boolean);
    if (filled.length <= 4) return filled;
    const step = Math.ceil(filled.length / 4);
    return filled.filter((_, index) => index % step === 0);
  }, [labels]);

  return (
    <figure className="tracker-chart-figure">
      <svg
        className="tracker-chart"
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={ariaLabel}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.24" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {areaPath && <path d={areaPath} fill={`url(#${gradientId})`} />}
        {linePath && <path className="tracker-chart__line" d={linePath} stroke={color} />}
      </svg>
      {visibleLabels.length > 0 && (
        <figcaption className="tracker-chart__labels">
          {visibleLabels.map((label, index) => (
            <span className="tracker-chart__label" key={`${label}-${index}`}>{label}</span>
          ))}
        </figcaption>
      )}
    </figure>
  );
}
